import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { GLOBAL_ENV_DEFAULT_PARAMS } from '../performance/schema.js';

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

/**
 * ThreeRenderer - WebGL renderer + shared post stack (bloom / output).
 * All scenes render through EffectComposer: RenderPass → UnrealBloomPass → OutputPass.
 */
export class ThreeRenderer {
  constructor(canvas) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setClearColor(0x000000);

    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 100);
    this.camera.position.z = 5;

    /** @type {number} */
    this._manualBloomStrength = Number(GLOBAL_ENV_DEFAULT_PARAMS.bloomStrength);
    /** @type {number} */
    this._manualBloomRadius = Number(GLOBAL_ENV_DEFAULT_PARAMS.bloomRadius);
    /** @type {number} */
    this._manualBloomThreshold = Number(GLOBAL_ENV_DEFAULT_PARAMS.bloomThreshold);

    this.resize();

    const s = this.renderer.getSize(new THREE.Vector2());
    this._renderPass = new RenderPass(new THREE.Scene(), this.camera);
    this._bloomPass = new UnrealBloomPass(
      new THREE.Vector2(s.x, s.y),
      this._manualBloomStrength,
      this._manualBloomRadius,
      this._manualBloomThreshold
    );
    this._composer = new EffectComposer(this.renderer);
    this._composer.addPass(this._renderPass);
    this._composer.addPass(this._bloomPass);
    this._composer.addPass(new OutputPass());
    this._composer.setSize(s.x, s.y);
  }

  resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this._composer) {
      this._composer.setSize(w, h);
      if (this._bloomPass) {
        this._bloomPass.resolution.set(w, h);
      }
    }
  }

  /**
   * Update bloom from global env / merged runtime params (call on preset change and slider edits).
   * @param {Record<string, unknown>} [params]
   */
  syncBloomFromParams(params = {}) {
    const d = GLOBAL_ENV_DEFAULT_PARAMS;
    const s = Number(params.bloomStrength);
    const r = Number(params.bloomRadius);
    const t = Number(params.bloomThreshold);
    this._manualBloomStrength = Number.isFinite(s) ? s : Number(d.bloomStrength);
    this._manualBloomRadius = Number.isFinite(r) ? r : Number(d.bloomRadius);
    this._manualBloomThreshold = Number.isFinite(t) ? t : Number(d.bloomThreshold);
    if (this._bloomPass) {
      this._bloomPass.strength = this._manualBloomStrength;
      this._bloomPass.radius = this._manualBloomRadius;
      this._bloomPass.threshold = this._manualBloomThreshold;
    }
  }

  /**
   * Per-frame MIDI modulation of bloom strength (matches former CA behavior).
   * @param {number | undefined} resolvedBloom01
   */
  applyFrameBloomModulation(resolvedBloom01) {
    if (!this._bloomPass) return;
    const base = Number.isFinite(this._manualBloomStrength)
      ? this._manualBloomStrength
      : Number(GLOBAL_ENV_DEFAULT_PARAMS.bloomStrength);
    if (resolvedBloom01 !== undefined && resolvedBloom01 !== null) {
      const c = clamp01(Number(resolvedBloom01));
      this._bloomPass.strength = c * 2 * base;
    } else {
      this._bloomPass.strength = base;
    }
    this._bloomPass.radius = this._manualBloomRadius;
    this._bloomPass.threshold = this._manualBloomThreshold;
  }

  /**
   * @param {{
   *   threeScene: import('three').Scene,
   *   getCamera?: () => import('three').Camera,
   * }} scene
   * @param {number} [deltaTime]
   */
  render(scene, deltaTime = 0) {
    const camera =
      typeof scene.getCamera === 'function' ? scene.getCamera() : this.camera;
    this._renderPass.scene = scene.threeScene;
    this._renderPass.camera = camera;
    this._composer.render(deltaTime);
  }

  destroy() {
    if (this._composer) {
      this._composer.renderTarget1?.dispose?.();
      this._composer.renderTarget2?.dispose?.();
      this._composer = null;
    }
    this._renderPass = null;
    this._bloomPass = null;
    this.renderer.dispose();
  }
}
